import React, { useState } from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const LANGUAGES = [
  'javascript', 'typescript', 'python', 'java', 'go', 'rust', 'c', 'cpp',
  'csharp', 'php', 'ruby', 'swift', 'kotlin', 'bash', 'sql', 'html', 'css',
  'json', 'yaml', 'xml', 'markdown', 'dockerfile', 'plaintext',
];

const CodeBlockComp: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const [copied, setCopied] = useState(false);
  const language = block.data?.language ?? 'plaintext';

  const handleCopy = () => {
    navigator.clipboard.writeText(block.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isEditing) {
    return (
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 bg-gray-800 px-4 py-2">
          <select
            value={language}
            onChange={(e) => onChange({ data: { ...block.data, language: e.target.value } })}
            className="text-xs bg-gray-700 text-gray-200 border border-gray-600 rounded px-2 py-1"
          >
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <span className="text-xs text-gray-400 ml-auto">Press Escape to finish</span>
        </div>
        <textarea
          autoFocus
          value={block.content}
          placeholder="// Enter code here..."
          onChange={(e) => onChange({ content: e.target.value })}
          onBlur={onFinish}
          onKeyDown={(e) => { if (e.key === 'Escape') onFinish(); }}
          rows={Math.max(4, (block.content.match(/\n/g) || []).length + 2)}
          className="w-full bg-gray-900 text-green-300 font-mono text-sm p-4 border-none outline-none resize-none"
          spellCheck={false}
        />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden group">
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{language}</span>
        <button
          onClick={handleCopy}
          className="text-xs text-gray-400 hover:text-white flex items-center gap-1 transition-colors"
        >
          {copied ? (
            <>
              <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="bg-gray-900 text-green-300 font-mono text-sm p-4 overflow-x-auto">
        <code>{block.content || '// Empty code block'}</code>
      </pre>
    </div>
  );
};

export default CodeBlockComp;
